import React, { useEffect } from "react";
import {
  FloatingBackButton,
  PageContainer,
  RoundedButton,
  Spinner,
  TextField,
  usePriceInput,
} from "@greeter/matter";
import { TitledInput } from "../TitledInput";
import { IconInput } from "../IconInput";
import { sunnyOutline, walletOutline } from "ionicons/icons";
import { UploadInput } from "../UploadInput";
import DescriptiveText from "../DescriptiveText";
import { FetchProduct, SaveProduct, UploadAsset } from "@greeter/venue-api";
import { useActor, useSelector } from "@xstate/react";
import {
  ProductChange,
  ProductEditorApiRequirements,
  productEditorMachine,
  provideApiForActor,
} from "./ProductEditorMachine";
import { useIonAlert, useIonToast } from "@ionic/react";
import { fromPromise, Subscription } from "xstate";

import css from "./ProductEditor.module.scss";
import { pushTo } from "@greeter/array";

function openSupportChatHandler(bookingId?: string, errorCode?: string) {
  return () => {
    window.open(`https://m.me/Greeternightlife`, "_blank");
  };
}

export type ProductEditorProps = React.PropsWithChildren & {
  productId?: string;
  onSaving?: () => Promise<void>;
  onSaved?: () => Promise<void>;
  onExit?: () => void;
  categoryId?: string;
  api: ProductEditorApiRequirements;
};

const tag = `[${ProductEditor.name}]`;
export function ProductEditor({
  productId,
  onSaving,
  onSaved,
  api,
  ...props
}: ProductEditorProps) {
  const [snap, send, m] = useActor(
    provideApiForActor(productEditorMachine, api),
    { input: productId ? { productId: productId } : undefined }
  );
  const state = useSelector(m, (s) => s.context.state.value);

  console.log(tag, "props.productId", productId);
  console.log(tag, "state", state);

  function change(change: ProductChange) {
    return send({ type: "change", data: change });
  }

  const [price, bind, setPriceInput] = usePriceInput({
    initialValue: 0,
  });

  const [cost, costBind, setCostInput] = usePriceInput({
    initialValue: 0,
  });

  useEffect(() => change({ type: "setPrice", price }), [price]);
  useEffect(() => change({ type: "setCost", cost }), [cost]);

  useEffect(() => {
    if (props.categoryId) {
      console.log("Setting the categoryId for the product", props.categoryId);
      change({ type: "setCategoryId", data: props.categoryId });
    }
  }, [props.categoryId]);

  useEffect(() => {
    if (!!state) {
      setPriceInput(state.price + "");
      setCostInput(state.cost + "");
    }
  }, [state]);

  const [showToast] = useIonToast();
  const [showAlert] = useIonAlert();

  useEffect(() => {
    const subscriptions: Array<Subscription> = [];
    if (onSaving) {
      subscriptions.push(m.on("saving", () => onSaving()));
    }
    if (onSaved) {
      subscriptions.push(m.on("saved", () => onSaved()));
      subscriptions.push(m.on("saved", () => showToast("Produkt gemt", 1_000)));
    }

    [
      m.on("saveProduct.failed", () =>
        showAlert({
          header: "Kunne ikke gemme produktet",
          message: "Det kan skyldes en manglende forbindelse til internettet",
          buttons: [
            { text: "Ok" },
            { text: "Skriv til support", handler: openSupportChatHandler() },
            {
              text: "Prøv igen",
              handler() {
                send({ type: "save" });
              },
            },
          ],
        })
      ),

      m.on("fetchProduct.failed", () =>
        showAlert({
          header: "Kunne ikke hente produktet",
          message:
            "Det kan skyldes en manglende forbindelse til internettet. Tjek at du har forbindelse til internettet ellers prøv igen senere",
          buttons: [
            { text: "Gå tilbage" },
            { text: "Skriv til support", handler: openSupportChatHandler() },
            {
              text: "Prøv igen",
              handler() {
                send({ type: "save" });
              },
            },
          ],
        })
      ),

      m.on("uploadAsset.failed", () =>
        showAlert({
          header: "Kunne ikke uploade billedet",
          message:
            "Det kan skyldes en manglende forbindelse til internettet. Tjek at du har forbindelse til internettet ellers prøv igen senere",
          buttons: [
            { text: "Gå tilbage" },
            { text: "Skriv til support", handler: openSupportChatHandler() },
            {
              text: "Prøv igen",
              handler() {
                send({ type: "uploadAsset" });
              },
            },
          ],
        })
      ),
    ].forEach(pushTo(subscriptions));

    return () => subscriptions.forEach((s) => s.unsubscribe());
  }, [m, alert, showToast, onSaved, onSaving]);

  useEffect(() => {
    const s = [
      m.on("uploadingAsset", () => console.log("Uploading asset")),
      m.on("uploadedAsset", () => console.log("Uploaded asset")),
    ];

    return () => s.forEach((s) => s.unsubscribe());
  }, [m]);

  return (
    <PageContainer>
      <FloatingBackButton
        onClick={(fallback) => {
          if (props.onExit) {
            return props.onExit();
          } else {
            fallback();
          }
        }}
      />
      <div className={css.ProductEditor}>
        <h3 style={{ margin: "3rem 1rem" }}>
          <b>
            <span style={{ color: "var(--gm-color-primary-highlight)" }}>
              <b>Produkt</b>
            </span>
          </b>
        </h3>
        <TitledInput required title="Navn">
          <IconInput
            className={css.New}
            value={state?.title ?? ""}
            onChange={(e: any) =>
              change({ type: "setTitle", title: e.target.value })
            }
            icon={sunnyOutline}
          />
        </TitledInput>
        <TitledInput required title="Billede">
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {state?.coverAsset?.uri && (
              <img className={css.Cover} src={state?.coverAsset?.uri} />
            )}
            <UploadInput
              className={css.New}
              accept="image/*"
              style={{ position: "relative" }}
              value={
                state?.coverAsset?.name ?? snap.context.fileToUpload?.name ?? ""
              }
              onFileChosen={(f) => {
                console.log("File to upload", f);
                if (f) {
                  send({ type: "setUploadAsset", data: f });
                  send({ type: "uploadAsset" });
                }
              }}
            />
          </div>
        </TitledInput>
        <TitledInput required title="Pris (med moms)">
          <IconInput className={css.New} {...bind} icon={walletOutline} />
        </TitledInput>
        <TitledInput required title="Kostpris (uden moms)">
          <IconInput className={css.New} {...costBind} icon={walletOutline} />
        </TitledInput>
        <TitledInput required title="Kort beskrivelse">
          <TextField
            className={css.New}
            value={state?.description ?? ""}
            onChange={(ev) => {
              change({
                type: "setDescription",
                description: ev.currentTarget.value,
              });
            }}
          />
        </TitledInput>
        <div style={{ margin: "0 1rem" }}>
          <RoundedButton
            active
            onClick={() => send({ type: "save" })}
            activeClassName={css.ActionButton}
          >
            Gem
          </RoundedButton>
        </div>
        <DescriptiveText>
          Har du rettelser, kan du altid <br />
          gå tilbage og redigere produktet
        </DescriptiveText>
      </div>
      <Spinner
        reason={
          snap.matches({ api: { product: "saving" } })
            ? "Gemmer"
            : snap.matches({ api: { product: "fetching" } }) &&
              "Henter produkt..."
        }
      />
    </PageContainer>
  );
}
